import {
  CloudWatchLogsClient,
  CreateLogStreamCommand,
  DescribeLogStreamsCommand,
  PutLogEventsCommand,
  PutLogEventsCommandInput,
} from '@aws-sdk/client-cloudwatch-logs'
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity'
import {
  CognitoIdentityCredentialProvider,
  CognitoIdentityCredentials,
  fromCognitoIdentityPool,
} from '@aws-sdk/credential-provider-cognito-identity'
import { Mutex } from 'async-mutex'
import { captureException } from '~/services/sentry'
import { getCognitoIdentityPoolId } from '~/utils/common'

const ID_POOL = getCognitoIdentityPoolId()
const REGION = 'us-west-2'
const SEND_DELAY = 10000 // 10 seconds

/**
 * Sends logs to CloudWatch when 10 seconds have elapsed.
 */
class CloudWatchLogger {
  private static _instance: CloudWatchLogger | null = null
  private _logGroupName: string
  private _logStreamName: string
  private _client: CloudWatchLogsClient
  private _credentialProvider: CognitoIdentityCredentialProvider
  private _cachedCredentials: CognitoIdentityCredentials | null = null
  private _logStreamCache: { [key: string]: boolean } = {}
  private _mutex: Mutex

  private _logBuffer: { message: string; timestamp: number }[] = []
  private _sendTimer: ReturnType<typeof setTimeout> | null = null

  private constructor(logGroupName: string, logStreamName: string) {
    this._mutex = new Mutex()
    this._logGroupName = logGroupName
    this._logStreamName = logStreamName

    this._credentialProvider = fromCognitoIdentityPool({
      identityPoolId: ID_POOL,
      client: new CognitoIdentityClient({ region: REGION }),
    })

    this._client = new CloudWatchLogsClient({
      region: REGION,
      credentials: this._credentialProvider,
    })
  }

  public static getInstance(
    logGroupName: string,
    logStreamName: string,
  ): CloudWatchLogger {
    if (CloudWatchLogger._instance === null) {
      CloudWatchLogger._instance = new CloudWatchLogger(
        logGroupName,
        logStreamName,
      )
    } else {
      CloudWatchLogger._instance._logStreamName = logStreamName
    }
    return CloudWatchLogger._instance
  }

  public async log(message: string): Promise<void> {
    const release = await this._mutex.acquire()
    try {
      this._logBuffer.push({
        message,
        timestamp: new Date().getTime(),
      })

      // Just ensure the send timer is started
      this.startSendTimer()
    } finally {
      release()
    }
  }

  private async sendLogs(): Promise<void> {
    const now = new Date().getTime()
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000

    const logsToSend = this._logBuffer
      .filter((log) => log.timestamp > twentyFourHoursAgo)
      .sort((a, b) => a.timestamp - b.timestamp)

    this._logBuffer = []

    if (logsToSend.length === 0) {
      if (this._sendTimer !== null) {
        clearTimeout(this._sendTimer)
        this._sendTimer = null
      }
      return
    }

    try {
      await this.refreshCredentialsIfNeeded()
      await this.ensureLogStreamExists()

      const params: PutLogEventsCommandInput = {
        logEvents: logsToSend,
        logGroupName: this._logGroupName,
        logStreamName: this._logStreamName,
      }

      const command = new PutLogEventsCommand(params)
      await this._client.send(command)
    } catch (err: unknown) {
      captureException(err)
      this._logBuffer.push(...logsToSend)
    } finally {
      if (this._sendTimer !== null) {
        clearTimeout(this._sendTimer)
        this._sendTimer = null
      }
    }

    this.startSendTimer()
  }

  public async flush(): Promise<void> {
    if (this._sendTimer !== null) {
      clearTimeout(this._sendTimer)
      this._sendTimer = null
    }

    await this.sendLogs()
  }

  private startSendTimer(): void {
    if (this._sendTimer === null) {
      this._sendTimer = setTimeout(async () => {
        await this.sendLogs()
      }, SEND_DELAY)
    }
  }

  private async refreshCredentialsIfNeeded(): Promise<void> {
    const release = await this._mutex.acquire()
    try {
      const bufferTimeMs = 5 * 60 * 1000 // 5 minute buffer

      // Check if we need new credentials
      const needNewCreds =
        !this._cachedCredentials ||
        (this._cachedCredentials.expiration &&
          this._cachedCredentials.expiration.getTime() - Date.now() <=
            bufferTimeMs)

      // If we need new credentials, fetch them
      if (needNewCreds) {
        this._cachedCredentials = await this._credentialProvider()

        // Update the CloudWatch client with the new credentials
        this._client = new CloudWatchLogsClient({
          region: REGION,
          credentials: this._cachedCredentials,
        })
      }
    } catch (err: unknown) {
      captureException(err)
      throw err
    } finally {
      release()
    }
  }

  private async logStreamExists(): Promise<boolean> {
    const command = new DescribeLogStreamsCommand({
      logGroupName: this._logGroupName,
      logStreamNamePrefix: this._logStreamName,
    })
    const response = await this._client.send(command)
    return (
      response.logStreams?.some(
        (stream) => stream.logStreamName === this._logStreamName,
      ) ?? false
    )
  }

  private async createLogStream(): Promise<void> {
    //if the log stream already exists, we don't need to create it and we can move on
    try {
      const command = new CreateLogStreamCommand({
        logGroupName: this._logGroupName,
        logStreamName: this._logStreamName,
      })
      await this._client.send(command)
    } catch (err: unknown) {
      if (
        err instanceof Error &&
        err.name === 'ResourceAlreadyExistsException'
      ) {
        return Promise.resolve()
      } else {
        captureException(err)
      }
    }
  }

  private async ensureLogStreamExists(): Promise<void> {
    if (!this._logStreamCache[this._logStreamName]) {
      if (await this.logStreamExists()) {
        this._logStreamCache[this._logStreamName] = true
      } else {
        await this.createLogStream()
        this._logStreamCache[this._logStreamName] = true
      }
    }
  }
}

export default CloudWatchLogger
